import React, { useEffect, useCallback, useMemo, useState, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { io, Socket } from 'socket.io-client';

// Import action creators with proper typing
import * as bookingActions from 'App/store/actions/booking.action';
import * as dashboardActions from 'App/store/actions/dashboard.action';
import * as authActions from 'App/store/actions/auth.action';
import * as callActions from 'App/store/actions/call.action';
import * as userActions from 'App/store/actions/user.action';
import { toastMessage } from 'App/store/actions/toast.action';
import { BUMP_SHARE_UNREAD, TOAST_MESSAGE } from 'App/store/constants';

// Define proper interfaces for type safety
interface User {
  id: number;
  type: 'rider' | 'driver' | 'agent';
  name: string;
  details?: {
    zone_id?: number;
  };
}

interface Location {
  latitude: number;
  longitude: number;
}

interface RootState {
  user: { user: User; currentLocation?: Location };
  booking: {
    zone: { zone_id: number };
    rider: { id: number };
    driver: { id: number };
    bookingId: number;
    chatMessageSend?: string;
    chat: any[];
    isChatOpen?: boolean;
    paymentConfirmationSend?: any;
  };
  socket: {
    socket: Socket | null;
  };
  call: CallData;
}

interface SocketProps {
  user: User;
  zone: { zone_id: number };
  bookingId: number;
  currentLocation?: Location;
  chatMessageSend?: string;
  rider: { id: number };
  driver: { id: number };
  chat: any[];
  call: CallData;
  sendIceCandidate?: any;
  callAnswer?: any;
  callStatus?: string;
  callOffer?: any;
  callEnd?: boolean;
  callAckMsg?: boolean;
  viciAckMsg?: any;
  isChatOpen?: boolean;
  paymentConfirmationSend?: any;
}

interface CallData {
  call: any;
  callType?: string;
  sendIceCandidate?: any;
  callAnswer?: any;
  callStatus?: string;
  callOffer?: any;
  callEnd?: boolean;
  callAckMsg?: boolean;
  viciAckMsg?: any;
  receiveViciAckMsg?: any;
  viciSession?: any;
}

interface BookingActionData {
  action: any;
}

const SocketComponent: React.FC<SocketProps> = ({
  user,
  zone,
  bookingId,
  currentLocation,
  chatMessageSend,
  rider,
  driver,
  chat,
  call,
  sendIceCandidate,
  callAnswer,
  callStatus,
  callOffer,
  callEnd,
  callAckMsg,
  viciAckMsg,
  isChatOpen = false,
  paymentConfirmationSend,
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const dispatch = useDispatch();

  // Use refs to track previous values and prevent unnecessary emissions
  // PHASE 0 (#5) — the socket effect depends only on user id/type, so it cannot
  // read `zone` directly without capturing a stale value. Mirror it into a ref
  // that stays current, and re-register when the zone first resolves or changes.
  const zoneIdRef = useRef<number | undefined>(undefined);

  const prevCallStatusRef = useRef<string | undefined>();
  const prevCallOfferRef = useRef<any>();
  const prevCallAnswerRef = useRef<any>();
  const prevCallAckMsgRef = useRef<boolean | undefined>();
  const prevViciAckMsgRef = useRef<any>();
  const prevCallEndRef = useRef<boolean | undefined>();
  const prevSendIceCandidateRef = useRef<any>();
  const listenersRegisteredRef = useRef(false);
  const lastSentMessageRef = useRef<string | null>(null);
  const prevPaymentConfirmationSendRef = useRef<any>();
  // Dedup the driver-cancellation toast — soc can re-emit the cancelled event
  // on reconnect, and we don't want a second toast for the same booking.
  const cancelToastBookingRef = useRef<any>(null);
  const socketRef = useRef<Socket | null>(null);
  // Debounce timer for the agent-dashboard live refetch. Booking sockets can
  // fire several status transitions in a burst; we coalesce them into a single
  // refetch (~400ms) so metrics/commission update once per burst, not per event.
  const agentRefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize socket once and reuse
  useEffect(() => {
    if (!user?.id) {
      return;
    }

    // If socket already exists and is connected, don't recreate
    if (socketRef.current?.connected) {
      return;
    }

    // Cleanup old socket if it exists
    if (socketRef.current) {
      console.log('Cleaning up old socket');
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const socketUrl = process.env.REACT_APP_SOC_API_URL;

    if (!socketUrl) {
      console.error('Socket URL is not defined in environment variables');
      return;
    }

    console.log('Creating new socket connection');
    const socketInstance = io(socketUrl, {
      transports: ['websocket', 'polling'],
      forceNew: false,
      timeout: 10000,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      randomizationFactor: 0.5,
    });

    const registerUser = () => {
      // PHASE 0 (#5) — send zoneId. Omitting it made the server evaluate
      // `undefined !== ''` as TRUE, so every web client joined the literal room
      // "zone_undefined": it received none of the real zone_<n> traffic (driver
      // location, ETA) and shared one cross-tenant room with every other zone.
      // Read from a ref: this effect only re-runs on user id/type, so a zone
      // that resolves later would otherwise be captured stale.
      const zoneId = zoneIdRef.current;
      socketInstance.emit('registerCustomer', {
        id: user.id,
        type: user.type,
        ...(zoneId ? { zoneId: String(zoneId) } : {}),
      });
    };

    socketInstance.on('connect', () => {
      console.log('Socket connected');
      setIsConnected(true);
      listenersRegisteredRef.current = false;
      dispatch({ type: 'SET_SOCKET', payload: socketInstance });
      registerUser();
    });

    socketInstance.on('reconnect', (attempt: number) => {
      console.log('Socket reconnected after', attempt, 'attempts');
      registerUser();
    });

    socketInstance.on('reconnect_attempt', (attempt: number) => {
      console.log('Socket reconnect attempt', attempt);
    });

    socketInstance.on('reconnect_error', (error: any) => {
      console.error('Socket reconnect error:', error);
    });

    socketInstance.on('forceLogout', (data: { hid: number }) => {
      try {
        const token = localStorage.getItem('userToken');
        if (!token) return;
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.hid === data.hid) {
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          localStorage.removeItem('userToken');
          window.location.reload();
        }
      } catch (e) {
        console.error('forceLogout handler error:', e);
      }
    });

    socketInstance.on('disconnect', (reason: any) => {
      console.log('Socket disconnected:', reason);
      setIsConnected(false);
      listenersRegisteredRef.current = false;
    });

    socketInstance.on('connect_error', (error: any) => {
      console.error('Socket connection error:', error);
      setIsConnected(false);
    });

    socketRef.current = socketInstance;

    return () => {
      console.log('Cleaning up socket on user change');
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      listenersRegisteredRef.current = false;
      if (agentRefetchTimerRef.current) {
        clearTimeout(agentRefetchTimerRef.current);
        agentRefetchTimerRef.current = null;
      }
    };
  }, [user?.id, user?.type, dispatch]);


  const socket = socketRef.current;
  // PHASE 0 (#5) — keep zoneIdRef fresh and re-register when the zone becomes
  // known (or changes), so the socket joins the correct zone_<n> room rather
  // than staying in whatever room it joined at connect time.
  useEffect(() => {
    const nextZoneId = zone?.zone_id ?? user?.details?.zone_id;
    if (zoneIdRef.current === nextZoneId) return;
    zoneIdRef.current = nextZoneId;
    if (socket && isConnected && user?.id && nextZoneId) {
      socket.emit('registerCustomer', {
        id: user.id,
        type: user.type,
        zoneId: String(nextZoneId),
      });
    }
  }, [zone?.zone_id, user?.details?.zone_id, socket, isConnected, user?.id, user?.type]);

  // Memoize event handlers to prevent recreation - only depend on dispatch
  const eventHandlers = useMemo(() => {
    // Coalesced live refresh for the agent dashboard. Only agents have the
    // dashboard open; riders/drivers ignore this. Refetches metrics + wallet +
    // commission ~400ms after the last booking event in a burst. The ongoing
    // rides panel already updates instantly from currentRidesList — this only
    // keeps the numeric cards/charts in sync without a manual refresh.
    const scheduleAgentRefetch = () => {
      if (user?.type !== 'agent') return;
      if (agentRefetchTimerRef.current) {
        clearTimeout(agentRefetchTimerRef.current);
      }
      agentRefetchTimerRef.current = setTimeout(() => {
        // These action creators call MakeApiRequest internally (which dispatches
        // its own PENDING/SUCCESS/FAILURE), so they're invoked directly — not
        // wrapped in dispatch(). Commission is credited on booking completion
        // and a withdrawal's balance/status can shift on the same events, so we
        // refresh metrics + wallet + commission + withdrawals together.
        dashboardActions.loadAgentDashboard();
        dashboardActions.loadAgentCurrentBalance();
        dashboardActions.loadAgentCommission();
        dashboardActions.loadAgentWithdrawals();
      }, 400);
    };

    return {
      initEvent: (data: any) => {
        dispatch(authActions.globalData(data));
      },

      bookingRider: (data: BookingActionData) => {
        dispatch(bookingActions.bookingDataUpdate(data.action));
        scheduleAgentRefetch();

        // Surface a notification when the DRIVER cancels (parity with mobile,
        // which pops an Alert). The status flips silently otherwise — the rider
        // may not be watching the screen. `cancelled_by !== user.id` excludes
        // the rider's own cancellation; dedup per booking guards reconnect re-emits.
        const action: any = data.action || {};
        const driverCancelled =
          action.status === 'cancelled' &&
          action.cancelled_by != null &&
          String(action.cancelled_by) !== String(user?.id);
        if (driverCancelled && cancelToastBookingRef.current !== action.booking_id) {
          cancelToastBookingRef.current = action.booking_id;
          dispatch({
            type: TOAST_MESSAGE,
            payload: {
              type: 'error',
              head: 'Booking cancelled',
              text: action.msg || 'Your booking was cancelled by the driver.',
            },
          });
        }
      },

      bookingDriver: (data: BookingActionData) => {
        dispatch(bookingActions.newCurrentRide(data.action));
        scheduleAgentRefetch();
      },

      // FR-10 — live driver marker on the rider's web map. Soc emits
      // this to the rider's id-room (joined via `registerCustomer`)
      // every time the driver phone reports a position. Reducer
      // normalises [lon, lat] → {lat, lng}; MapRoute renders the
      // marker and re-centres the map on each tick.
      bookingMessageDriverLoc: (data: BookingActionData) => {
        dispatch(bookingActions.setDriverLiveLocation(data.action));
      },

      // PHASE 0 (#12) — parity with mobile. soc emits both of these
      // (soc/routes/index.js:1618 and :352) and the mobile app handles them;
      // web listened to neither, so the same trip showed a bare marker and a
      // frozen ETA on web while mobile drew the route trail and a live ETA.
      saveCurrentBookingTrack: (data: BookingActionData) => {
        dispatch(bookingActions.setDriverTrail(data.action));
      },

      etaUpdate: (data: any) => {
        dispatch(bookingActions.setDriverEta(data?.action ?? data));
      },

      // Payment method change broadcast (rider→driver). soc relays the rider's
      // selected gateway as { action: <gateway string> }. The reducer
      // normalizes it to the { id } shape the driver payment screen reads, so
      // the driver flips between "please wait" (card/applepay/etc.) and
      // "Collect Payment" (cash) the instant the rider chooses.
      paymentTypeConfirmation: (data: any) => {
        if (data?.action != null) {
          dispatch(bookingActions.setPaymentConfirmation(data.action));
        }
      },

      callInitiate: (data: any) => {
        console.log('Socket callInitiate received:', data);
        dispatch(callActions.updateCallData(data.action.call));
        dispatch(callActions.setReceiveCallOffer(data.action.callOffer));
      },

      callAnswer: (data: any) => {
        console.log('Socket callAnswer received:', data);
        dispatch(callActions.setAnswerReceivedCall(data.action.callAnswer));
      },

      callAcknowledge: () => {
        console.log('Socket callAcknowledge received');
        dispatch(callActions.receiveAcknowledge());
      },

      callEnd: () => {
        console.log('Socket callEnd received');
        dispatch(callActions.setEndCall());
      },

      exchangeIceCandidate: (data: any) => {
        if (data.action) {
          console.log('Socket exchangeIceCandidate received:', data);
          dispatch(callActions.setReceiveIceCandidate(data.action));
        }
      },

      bookingMessage: (data: BookingActionData) => {
        // Check for duplicate before dispatching
        if (lastSentMessageRef.current === data.action?.msg) {
          lastSentMessageRef.current = null;
        }

        dispatch(bookingActions.fetchChatMessage(data.action));

        // Show toast notification if chat is closed and message is from another user
        if (!isChatOpen && data.action?.id !== user?.id) {
          const messagePreview = data.action?.msg?.length > 50
            ? `${data.action.msg.substring(0, 50)}...`
            : data.action.msg;

          const senderName = data.action?.name || (user?.type === 'rider' ? 'Driver' : 'Rider');

          dispatch(toastMessage({
            type: 'info',
            head: `New message from ${senderName}`,
            text: messagePreview || 'You have a new message'
          }));
        }
      },

      vicidialCallAssigned: (data: any) => {
        console.log('');
        console.log('╔════════════════════════════════════════════════════════════╗');
        console.log('║  FRONTEND: vicidialCallAssigned RECEIVED                   ║');
        console.log('╚════════════════════════════════════════════════════════════╝');
        console.log('📥 Full data:', JSON.stringify(data, null, 2));
        console.log('');
        console.log('📋 Session Info:');
        console.log('   • Session ID:', data?.session_id);
        console.log('   • From ID (caller):', data?.from_id);
        console.log('   • From Name:', data?.from_name);
        console.log('   • From Number:', data?.from_number?.username);
        console.log('   • To ID (receiver):', data?.to_id);
        console.log('   • To Name:', data?.to_name);
        console.log('   • To Number:', data?.to_number?.username);
        console.log('');
        console.log('👤 Current User:');
        console.log('   • User ID:', user?.id);
        console.log('   • User Name:', user?.name);
        console.log('   • User Type:', user?.type);
        console.log('');

        const isToUser = String(data?.to_id) === String(user?.id);
        const isFromUser = String(data?.from_id) === String(user?.id);
        console.log('🔍 Role Detection:');
        console.log('   • Is TO user (receiver)?:', isToUser);
        console.log('   • Is FROM user (caller)?:', isFromUser);
        console.log('');

        dispatch(callActions.setViciSession(data));
        console.log('✅ Redux: setViciSession dispatched');

        // If current user is the TO (callee), set up the incoming call data
        // so the VICIDial component renders for them
        if (data && isToUser) {
          console.log('');
          console.log('📞 THIS USER IS THE RECEIVER - Setting up incoming call UI...');
          const incomingCallData = {
            type: 'vici',
            status: 'incoming',
            id: data.session_id,
            from: {
              id: data.from_id,
              name: data.from_name || 'Caller',
              mobile: data.from_number?.username,
            },
            to: {
              id: data.to_id,
              name: data.to_name || user?.name || 'Unknown',
              mobile: data.to_number?.username,
            },
          };
          console.log('   Incoming call data:', JSON.stringify(incomingCallData, null, 2));
          dispatch(callActions.updateCallData(incomingCallData));
          console.log('✅ Redux: updateCallData dispatched - VICIDial should now render!');
          console.log('   Next: User should see Accept/Decline buttons');
        } else if (isFromUser) {
          console.log('');
          console.log('📞 THIS USER IS THE CALLER - Waiting for receiver to accept...');
          console.log('   VICIDial should show "Calling..." status');
        }
        console.log('════════════════════════════════════════════════════════════════');
        console.log('');
      },

      vicidialCallEnded: (data: any) => {
        console.log('');
        console.log('╔════════════════════════════════════════════════════════════╗');
        console.log('║  FRONTEND: vicidialCallEnded RECEIVED                      ║');
        console.log('╚════════════════════════════════════════════════════════════╝');
        console.log('📥 Data:', data);
        console.log('🔄 Clearing viciSession and ending call...');
        dispatch(callActions.clearViciSessionState());
        dispatch(callActions.setEndCall());
        console.log('✅ Call ended and state cleared');
        console.log('════════════════════════════════════════════════════════════════');
        console.log('');
      },

      viciAcknowledge: (data: any) => {
        console.log('');
        console.log('╔════════════════════════════════════════════════════════════╗');
        console.log('║  FRONTEND: viciAcknowledge RECEIVED (Caller side)          ║');
        console.log('╚════════════════════════════════════════════════════════════╝');
        console.log('📥 Data:', JSON.stringify(data, null, 2));
        console.log('');
        console.log('🎉 This means the RECEIVER has accepted the call!');
        console.log('   Caller should now start the SIP call...');
        console.log('');
        dispatch(callActions.receiveViciAcknowledge(data));
        console.log('✅ Redux: receiveViciAcknowledge dispatched');
        console.log('   Next: Auto-call logic should trigger makeCall()');
        console.log('════════════════════════════════════════════════════════════════');
        console.log('');
      },

      // Phase 2 — vehicle-share lifecycle event. Soc emits one event
      // per affected party (sender + recipient) for each step:
      // invite_received / invite_accepted / invite_rejected /
      // invite_cancelled / abort_requested / share_terminated.
      //
      // We do three things:
      //  1. Optimistic +1 on `invite_received` so the header badge
      //     updates before the fetch resolves. Other events are
      //     resolutions of existing rows — they shouldn't bump.
      //  2. Toast (with title + body) so the user is informed when
      //     they're not on the share screen. The screen itself
      //     listens to slice updates.
      //  3. Re-fetch shareNotifications. The fulfilled handler
      //     overwrites the optimistic count with the authoritative
      //     pending-received total.
      vehicleShare: (payload: any) => {
        try {
          if (payload?.event === 'invite_received') {
            dispatch({ type: BUMP_SHARE_UNREAD });
          }
          if (payload?.title && payload?.body) {
            dispatch(
              toastMessage({
                type: 'info',
                head: payload.title,
                text: payload.body,
              }),
            );
          }
          // userActions.shareNotifications is fire-and-forget through
          // MakeApiRequest; no need to await.
          userActions.shareNotifications();
          // QA recheck (W4) — share lifecycle events change the
          // recipient's `acceptedVehicle` (on accept) and the
          // sender's `vehicles[].isShared` flags (on accept/reject/
          // takeback). Refreshing only the share-notifications list
          // left MainPageDetails stale until manual reload. Refresh
          // the fleet for any non-`invite_received` event (the
          // initial invite doesn't change either side's vehicle
          // flags — the row is still pending on both sides).
          if (payload?.event && payload.event !== 'invite_received') {
            userActions.vehiclesList();
          }
        } catch (e) {
          console.error('vehicleShare handler error:', e);
        }
      },
    };
  }, [dispatch, isChatOpen, user]);

  // Register socket listeners whenever connected (including after reconnect)
  useEffect(() => {
    if (!socket || !isConnected) return;

    // Remove any stale listeners before re-registering to avoid duplicates
    Object.entries(eventHandlers).forEach(([event, handler]) => {
      socket.off(event, handler);
      socket.on(event, handler);
    });

    listenersRegisteredRef.current = true;
    console.log('Socket listeners registered');

    return () => {
      Object.entries(eventHandlers).forEach(([event, handler]) => {
        socket.off(event, handler);
      });
      listenersRegisteredRef.current = false;
    };
  }, [socket, isConnected, eventHandlers]);

  // Handle call initiation with duplicate prevention
  useEffect(() => {
    if (
      callStatus === 'initiate' &&
      callOffer &&
      socket &&
      isConnected &&
      (prevCallStatusRef.current !== callStatus || prevCallOfferRef.current !== callOffer)
    ) {
      console.log('Emitting callInitiate');
      socket.emit('callInitiate', { call: call.call, callOffer: callOffer });
      prevCallStatusRef.current = callStatus;
      prevCallOfferRef.current = callOffer;
    }
  }, [callStatus, callOffer, socket, isConnected, call]);

  // Handle call answer with duplicate prevention
  useEffect(() => {
    if (
      callAnswer &&
      socket &&
      isConnected &&
      prevCallAnswerRef.current !== callAnswer
    ) {
      console.log('Emitting callAnswer');
      socket.emit('callAnswer', { call: call.call, callAnswer: callAnswer });
      prevCallAnswerRef.current = callAnswer;
    }
  }, [callAnswer, socket, isConnected, call]);

  // Handle call acknowledgment with duplicate prevention
  useEffect(() => {
    if (
      callAckMsg &&
      socket &&
      isConnected &&
      prevCallAckMsgRef.current !== callAckMsg
    ) {
      console.log('Emitting callAcknowledge');
      socket.emit('callAcknowledge', { call: call.call });
      prevCallAckMsgRef.current = callAckMsg;
    }
  }, [callAckMsg, socket, isConnected, call]);

  // Handle VICIDial acknowledgment with duplicate prevention
  // This is triggered when the RECEIVER accepts the call
  useEffect(() => {
    if (
      viciAckMsg &&
      socket &&
      isConnected &&
      prevViciAckMsgRef.current !== viciAckMsg
    ) {
      console.log('');
      console.log('╔════════════════════════════════════════════════════════════╗');
      console.log('║  FRONTEND: EMITTING viciAcknowledge (Receiver side)        ║');
      console.log('╚════════════════════════════════════════════════════════════╝');
      console.log('📤 Sending acknowledgement to backend...');
      console.log('   Payload:', JSON.stringify(viciAckMsg, null, 2));
      console.log('');
      socket.emit('viciAcknowledge', viciAckMsg);
      console.log('✅ viciAcknowledge EMITTED to backend');
      console.log('   Backend will forward this to the caller (from_id)');
      console.log('════════════════════════════════════════════════════════════════');
      console.log('');
      prevViciAckMsgRef.current = viciAckMsg;
    }
  }, [viciAckMsg, socket, isConnected]);

  // Handle call end with duplicate prevention
  useEffect(() => {
    if (
      callEnd &&
      socket &&
      isConnected &&
      prevCallEndRef.current !== callEnd
    ) {
      console.log('Emitting callEnd');
      socket.emit('callEnd', { call: call.call, callEnd: true });
      prevCallEndRef.current = callEnd;
    }
  }, [callEnd, socket, isConnected, call]);

  // Handle ICE candidate exchange with duplicate prevention
  useEffect(() => {
    if (
      call &&
      sendIceCandidate &&
      socket &&
      isConnected &&
      prevSendIceCandidateRef.current !== sendIceCandidate
    ) {
      let id = call.call?.to.id;
      if (call.callType === 'incoming') {
        id = call.call?.from.id;
      }

      console.log('Emitting exchangeIceCandidate');
      socket.emit('exchangeIceCandidate', {
        id: id,
        iceCandidate: sendIceCandidate,
      });
      prevSendIceCandidateRef.current = sendIceCandidate;
    }
  }, [call, sendIceCandidate, socket, isConnected]);

  // Handle chat message sending
  useEffect(() => {
    if (!socket || !isConnected || !chatMessageSend || !user) return;

    const toId = user.type === 'rider' ? driver?.id : rider?.id;
    if (!toId) return;

    // Prevent sending duplicate messages
    if (lastSentMessageRef.current === chatMessageSend) return;

    lastSentMessageRef.current = chatMessageSend;
    const currentTime = new Date().getTime();

    socket.emit('bookingMessage', {
      booking_id: bookingId,
      id: user.id,
      to_id: toId,
      name: user.name,
      msg: chatMessageSend,
      time: currentTime,
    });
  }, [socket, isConnected, chatMessageSend, user, bookingId, rider, driver]);

  // Handle location updates (throttled)
  useEffect(() => {
    if (!socket || !isConnected || !currentLocation || !user?.id) return;

    const locationData = {
      latitude: currentLocation.latitude,
      longitude: currentLocation.longitude,
      type: user.type,
      custId: user.id,
      ...(bookingId && user.type === 'driver' ? { booking_id: bookingId } : {}),
    };

    // Throttle location updates to prevent spam
    const throttleTimeout = setTimeout(() => {
      socket.emit('currentLocation', locationData);
    }, 1000);

    return () => clearTimeout(throttleTimeout);
  }, [socket, isConnected, currentLocation, user, bookingId]);

  // Payment method change → broadcast to the driver (parity with mobile).
  // When the rider selects a method, PaymentView dispatches
  // setPaymentConfirmationSend({ gateway, id: driverId }); we emit it to soc,
  // which relays `paymentTypeConfirmation` into the driver's room so their
  // screen flips between "please wait" and "Collect Payment" immediately.
  useEffect(() => {
    if (!socket || !isConnected || !paymentConfirmationSend) return;
    if (prevPaymentConfirmationSendRef.current === paymentConfirmationSend) return;
    prevPaymentConfirmationSendRef.current = paymentConfirmationSend;
    socket.emit('paymentTypeConfirmation', paymentConfirmationSend);
  }, [socket, isConnected, paymentConfirmationSend]);

  return null;
};

const mapStateToProps = (state: RootState) => ({
  user: state.user.user,
  zone: state.booking.zone,
  rider: state.booking.rider,
  driver: state.booking.driver,
  bookingId: state.booking.bookingId,
  currentLocation: state.user.currentLocation,
  chatMessageSend: state.booking.chatMessageSend,
  chat: state.booking.chat,
  call: state.call,
  sendIceCandidate: state.call.sendIceCandidate,
  callAnswer: state.call.callAnswer,
  callStatus: state.call.callStatus,
  callOffer: state.call.callOffer,
  callEnd: state.call.callEnd,
  callAckMsg: state.call.callAckMsg,
  viciAckMsg: state.call.viciAckMsg,
  isChatOpen: state.booking.isChatOpen,
  paymentConfirmationSend: state.booking.paymentConfirmationSend,
});

export default connect(mapStateToProps)(SocketComponent);
